import {
    ComplementaryBedTypeState,
    ComplementaryCityState,
    ComplementaryCountryState,
    ComplementaryCurrencyState,
    ComplementaryHostSegmentState,
    ComplementaryHotelCategoryState,
    ComplementaryHotelChainState,
    ComplementaryHotelTypeState,
    ComplementaryImageTypeState,
    ComplementaryLanguageState,
    ComplementaryMealPlanTypeState,
    ComplementaryProvinceState,
    ComplementaryRegionState,
    ComplementaryServiceCategoryState,
    ComplementaryServiceTypeState,
    ComplementaryTimeZoneState,
} from '../../types/properties/propertyTypes';
import { readOnlyEntities } from '../../utils/commonVariables/entityKeys';
import { buildOdataEndPoint } from '../../utils/odataEndPoint';
import { GAELIC_CONTENT_LANGUAGE_KEY } from '../../utils/property/propertyUtils';
import { cachedApi } from '../api';

export const getCountries = async () => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.Country,
        select: 'Id,Name,Caption,LocalizedCaption',
        filter: 'Active',
        orderBy: 'Caption asc',
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map(
            (item: {
                Id: string;
                Name: string;
                LocalizedCaption: string;
                Caption: string;
            }) => ({
                id: item.Id,
                name: item.LocalizedCaption || item.Caption || item.Name,
            })
        )
        .sort((a: ComplementaryCountryState, b: ComplementaryCountryState) =>
            a.name.localeCompare(b.name)
        );
};

export const getRegions = async (idCountry: number) => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.Region,
        select: 'Id,Name',
        filter: `IdCountry eq ${idCountry}`,
        orderBy: 'Name asc',
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map((item: { Id: string; Name: string }) => ({
            id: item.Id,
            name: item.Name,
        }))
        .sort((a: ComplementaryRegionState, b: ComplementaryRegionState) =>
            a.name.localeCompare(b.name)
        );
};

export const getProvinces = async (idCountry: number) => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.Province,
        select: 'Id,Name',
        filter: `IdCountry eq ${idCountry}`,
        orderBy: 'Name asc',
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map((item: { Id: string; Name: string }) => ({
            id: item.Id,
            name: item.Name,
        }))
        .sort((a: ComplementaryProvinceState, b: ComplementaryProvinceState) =>
            a.name.localeCompare(b.name)
        );
};

export const getCities = async (idCountry: number) => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.City,
        select: 'Id,Name',
        filter: `IdCountry eq ${idCountry}`,
        orderBy: 'Name asc',
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map((item: { Id: string; Name: string }) => ({
            id: item.Id,
            name: item.Name,
        }))
        .sort((a: ComplementaryCityState, b: ComplementaryCityState) =>
            a.name.localeCompare(b.name)
        );
};

export const getTimezones = async () => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.TimeZoneUtil,
        select: 'Id,DisplayName',
        orderBy: 'DisplayName asc',
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map((item: { Id: string; DisplayName: string }) => ({
            id: item.Id,
            name: item.DisplayName,
        }))
        .sort((a: ComplementaryTimeZoneState, b: ComplementaryTimeZoneState) =>
            a.name.localeCompare(b.name)
        );
};

export const getHotelTypes = async () => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.HotelType,
        select: 'Id,Id,Caption,LocalizedCaption,Name',
        filter: 'Enabled',
        orderBy: 'LocalizedCaption asc',
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map(
            (item: {
                Id: string;
                Name: string;
                LocalizedCaption: string;
                Caption: string;
            }) => ({
                id: item.Id,
                name: item.LocalizedCaption || item.Caption || item.Name,
            })
        )
        .sort(
            (a: ComplementaryHotelTypeState, b: ComplementaryHotelTypeState) =>
                a.name.localeCompare(b.name)
        );
};

export const getHotelCategories = async () => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.HotelCategory,
        select: 'Id,Caption,LocalizedCaption,Name',
        filter: 'Enabled',
        orderBy: 'LocalizedCaption asc',
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map(
            (item: {
                Id: string;
                Name: string;
                LocalizedCaption: string;
                Caption: string;
            }) => ({
                id: item.Id,
                name: item.LocalizedCaption || item.Caption || item.Name,
            })
        )
        .sort(
            (
                a: ComplementaryHotelCategoryState,
                b: ComplementaryHotelCategoryState
            ) => a.name.localeCompare(b.name)
        );
};

export const getHotelChains = async () => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.HotelChain,
        select: 'Id,Name',
        filter: 'Enabled',
        orderBy: 'Name asc',
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map((item: { Id: string; Name: string }) => ({
            id: item.Id,
            name: item.Name,
        }))
        .sort(
            (
                a: ComplementaryHotelChainState,
                b: ComplementaryHotelChainState
            ) => a.name.localeCompare(b.name)
        );
};

export const getCurrencies = async () => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.Currency,
        select: 'Id,Name',
        filter: 'Enabled',
        orderBy: 'Name asc',
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map((item: { Id: string; Name: string }) => ({
            id: item.Id,
            name: item.Name,
        }))
        .sort((a: ComplementaryCurrencyState, b: ComplementaryCurrencyState) =>
            a.name.localeCompare(b.name)
        );
};

export const getServiceCategories = async () => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.ServiceCategory,
        select: 'Id,Name,Caption,LocalizedCaption',
        filter: ['Enabled', 'ForHotel'],
        orderBy: 'Name asc',
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map(
            (category: {
                Id: string;
                Name: string;
                LocalizedCaption: string;
                Caption: string;
            }) => ({
                id: category.Id,
                name:
                    category.LocalizedCaption ||
                    category.Caption ||
                    category.Name,
            })
        )
        .sort(
            (
                a: ComplementaryServiceCategoryState,
                b: ComplementaryServiceCategoryState
            ) => a.name.localeCompare(b.name)
        );
};

export const getServiceTypes = async () => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.ServiceType,
        select: 'Id,LocalizedCaption,Caption,Name,IdServiceCategory,Free',
        filter: ['Enabled', 'ForHotel', 'ServiceCategory/Enabled'],
        orderBy: 'Name asc',
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map(
            (type: {
                Id: string;
                Name: string;
                LocalizedCaption: string;
                Caption: string;
                IdServiceCategory: number;
                Free: boolean;
            }) => ({
                id: type.Id,
                name: type.LocalizedCaption || type.Caption || type.Name,
                idServiceCategory: type.IdServiceCategory,
                free: type.Free,
            })
        )
        .sort(
            (
                a: ComplementaryServiceTypeState,
                b: ComplementaryServiceTypeState
            ) => a.name.localeCompare(b.name)
        );
};

export const getRoomServiceCategories = async () => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.ServiceCategory,
        select: 'Id,Name,Caption,LocalizedCaption',
        filter: ['Enabled', 'ForRoomType'],
        orderBy: 'Name asc',
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map(
            (category: {
                Id: string;
                Name: string;
                LocalizedCaption: string;
                Caption: string;
            }) => ({
                id: category.Id,
                name:
                    category.LocalizedCaption ||
                    category.Caption ||
                    category.Name,
            })
        )
        .sort(
            (
                a: ComplementaryServiceCategoryState,
                b: ComplementaryServiceCategoryState
            ) => a.name.localeCompare(b.name)
        );
};

export const getRoomServiceTypes = async () => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.ServiceType,
        select: 'Id,LocalizedCaption,Caption,Name,IdServiceCategory,Free',
        filter: ['Enabled', 'ForRoomType', 'ServiceCategory/Enabled'],
        orderBy: 'Name asc',
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map(
            (type: {
                Id: string;
                Name: string;
                LocalizedCaption: string;
                Caption: string;
                IdServiceCategory: number;
                Free: boolean;
            }) => ({
                id: type.Id,
                name: type.LocalizedCaption || type.Caption || type.Name,
                idServiceCategory: type.IdServiceCategory,
                free: type.Free,
            })
        )
        .sort(
            (
                a: ComplementaryServiceTypeState,
                b: ComplementaryServiceTypeState
            ) => a.name.localeCompare(b.name)
        );
};

export const getHostSegments = async () => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.HostSegment,
        select: 'Id,Caption,LocalizedCaption,Name',
        filter: 'Enabled',
        orderBy: 'Name asc',
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map(
            (segment: {
                Id: string;
                Name: string;
                LocalizedCaption: string;
                Caption: string;
            }) => ({
                id: segment.Id,
                name:
                    segment.LocalizedCaption || segment.Caption || segment.Name,
            })
        )
        .sort(
            (
                a: ComplementaryHostSegmentState,
                b: ComplementaryHostSegmentState
            ) => a.name.localeCompare(b.name)
        );
};

export const getMealPlanTypes = async () => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.MealPlanType,
        select: 'Id,Caption,LocalizedCaption,Name,MappingCodeTemplate',
        filter: 'Enabled',
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map(
            (type: {
                Id: string;
                Name: string;
                LocalizedCaption: string;
                Caption: string;
                MappingCodeTemplate: string;
            }) => ({
                id: type.Id,
                name: type.LocalizedCaption || type.Caption || type.Name,
                mappingCodeTemplate: type.MappingCodeTemplate || '',
            })
        )
        .sort(
            (
                a: ComplementaryMealPlanTypeState,
                b: ComplementaryMealPlanTypeState
            ) => a.name.localeCompare(b.name)
        );
};

export const getImageTypes = async () => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.HotelImageType,
        select: 'Id,Caption,LocalizedCaption,Name,Key',
        filter: ['Enabled'],
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map(
            (type: {
                Id: string;
                Name: string;
                LocalizedCaption: string;
                Caption: string;
                Key: string;
            }) => ({
                id: type.Id,
                name: type.LocalizedCaption || type.Caption || type.Name,
                nameKey: type.Key,
            })
        )
        .sort(
            (a: ComplementaryImageTypeState, b: ComplementaryImageTypeState) =>
                a.name.localeCompare(b.name)
        );
};

export const getDescriptionLanguages = async () => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.ContentLanguage,
        select: 'Id,Caption,LocalizedCaption,Name,CultureId',
        filter: [
            'Enabled',
            `Key ne '${GAELIC_CONTENT_LANGUAGE_KEY}'`,
            // `Key ne '${ENGLISH_CONTENT_LANGUAGE_KEY}'`,
        ],
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map(
            (lang: {
                Id: string;
                Name: string;
                LocalizedCaption: string;
                Caption: string;
                CultureId: string;
            }): ComplementaryLanguageState => ({
                id: lang.Id,
                name: lang.LocalizedCaption || lang.Caption || lang.Name,
                cultureId: lang.CultureId,
            })
        )
        .sort((a: ComplementaryLanguageState, b: ComplementaryLanguageState) =>
            a.name.localeCompare(b.name)
        );
};

export const getBedTypes = async () => {
    const endpoint = buildOdataEndPoint({
        entity: readOnlyEntities.BedType,
        select: 'Id,Name,Caption,LocalizedCaption',
        filter: 'Enabled',
        orderBy: 'LocalizedCaption asc',
    });
    const { data } = await cachedApi.get(endpoint);
    return data.value
        .map(
            (category: {
                Id: string;
                Name: string;
                LocalizedCaption: string;
                Caption: string;
            }) => ({
                id: category.Id,
                name:
                    category.LocalizedCaption ||
                    category.Caption ||
                    category.Name,
            })
        )
        .sort((a: ComplementaryBedTypeState, b: ComplementaryBedTypeState) =>
            a.name.localeCompare(b.name)
        );
};
